"use client";

import { useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { FileUp, Loader2, Lock, Paperclip } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useUploadRegistrationFile } from "../hooks";
import {
  REGISTRATION_FILE_ACCEPT,
  REGISTRATION_FILE_MAX_BYTES,
  REGISTRATION_TEXT_MAX,
  answersFromDraft,
  draftProblems,
  fileSizeLabel,
  type RegistrationAnswerInput,
  type RegistrationDraft,
  type RegistrationQuestion,
} from "../registration";

/**
 * The organizer's questions at the door - `EX-REG01`, the canvas board
 * `Registration` (its phone, "step 2 of 2"). Used twice: in the registration
 * dialog, and when a registered contestant is asked a question the organizer
 * added later. A file is uploaded the moment it is picked, so the submit only
 * carries its id; the server checks everything again.
 */
export function RegistrationAnswersForm({
  slug,
  questions,
  initial,
  submitLabel,
  pending,
  error,
  onSubmit,
}: {
  slug: string;
  questions: readonly RegistrationQuestion[];
  initial?: RegistrationDraft;
  submitLabel: string;
  pending: boolean;
  error: string | null;
  onSubmit: (answers: RegistrationAnswerInput[]) => void;
}) {
  const t = useTranslations("contest.page.registrationQuestions");
  const [draft, setDraft] = useState<RegistrationDraft>(initial ?? {});
  const [touched, setTouched] = useState(false);
  const problems = draftProblems(questions, draft);
  const set = (id: string, value: RegistrationDraft[string]) =>
    setDraft((state) => ({ ...state, [id]: { ...state[id], ...value } }));

  return (
    <form
      className="space-y-5"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        setTouched(true);
        if (problems.size === 0) onSubmit(answersFromDraft(questions, draft));
      }}
      data-testid="registration-answers-form"
    >
      {questions.map((question) => {
        const id = `registration-answer-${question.id}`;
        const invalid = touched && problems.has(question.id);
        const describedBy = question.help ? `${id}-help` : undefined;
        return (
          <div key={question.id} className="space-y-2">
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              {question.kind === "choice" ? (
                <span id={`${id}-label`} className="text-sm font-semibold text-neutral-900">
                  {question.label}
                </span>
              ) : (
                <label htmlFor={id} className="text-sm font-semibold text-neutral-900">
                  {question.label}
                </label>
              )}
              {!question.required ? <span className="text-xs text-neutral-500">{t("optional")}</span> : null}
            </div>
            {question.help ? (
              <p id={`${id}-help`} className="text-xs leading-relaxed text-neutral-500">
                {question.help}
              </p>
            ) : null}

            {question.kind === "text" ? (
              <Input
                id={id}
                value={draft[question.id]?.text ?? ""}
                maxLength={REGISTRATION_TEXT_MAX}
                aria-invalid={invalid || undefined}
                aria-describedby={describedBy}
                onChange={(event) => set(question.id, { text: event.target.value })}
              />
            ) : null}

            {question.kind === "number" ? (
              <Input
                id={id}
                type="number"
                inputMode="decimal"
                value={draft[question.id]?.number ?? ""}
                {...(question.min !== null ? { min: question.min } : {})}
                {...(question.max !== null ? { max: question.max } : {})}
                aria-invalid={invalid || undefined}
                aria-describedby={describedBy}
                onChange={(event) => set(question.id, { number: event.target.value })}
              />
            ) : null}

            {question.kind === "choice" ? (
              <div
                role="radiogroup"
                aria-labelledby={`${id}-label`}
                aria-describedby={describedBy}
                className="flex flex-wrap gap-2"
              >
                {question.options.map((option) => {
                  const picked = draft[question.id]?.choice === option;
                  return (
                    <Button
                      key={option}
                      type="button"
                      variant="choice"
                      size="md"
                      role="radio"
                      aria-checked={picked}
                      onClick={() => set(question.id, { choice: picked ? "" : option })}
                    >
                      {option}
                    </Button>
                  );
                })}
              </div>
            ) : null}

            {question.kind === "file" ? (
              <FileAnswer
                id={id}
                slug={slug}
                questionId={question.id}
                file={draft[question.id]?.file ?? null}
                invalid={invalid}
                onChange={(file) => set(question.id, { file })}
              />
            ) : null}

            {invalid ? (
              <p className="text-xs text-[rgb(var(--destructive))]" role="alert">
                {question.kind === "number" && draft[question.id]?.number?.trim()
                  ? t("outOfRange", { min: question.min ?? "-", max: question.max ?? "-" })
                  : t("requiredMissing")}
              </p>
            ) : null}
          </div>
        );
      })}

      <p className="flex items-start gap-2 text-xs leading-relaxed text-neutral-500">
        <Lock className="mt-0.5 h-3.5 w-3.5 flex-none" aria-hidden />
        {t("privacy")}
      </p>
      {error ? (
        <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700" role="alert">
          {error}
        </p>
      ) : null}
      <Button type="submit" variant="primary" size="lg" className="w-full" disabled={pending}>
        {pending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
        {submitLabel}
      </Button>
    </form>
  );
}

function FileAnswer({
  id,
  slug,
  questionId,
  file,
  invalid,
  onChange,
}: {
  id: string;
  slug: string;
  questionId: string;
  file: RegistrationDraft[string]["file"];
  invalid: boolean;
  onChange: (file: RegistrationDraft[string]["file"]) => void;
}) {
  const t = useTranslations("contest.page.registrationQuestions");
  const upload = useUploadRegistrationFile(slug);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  const pick = async (picked: File | undefined) => {
    if (!picked) return;
    setError(null);
    if (picked.size > REGISTRATION_FILE_MAX_BYTES) {
      setError(t("fileTooLarge"));
      return;
    }
    try {
      onChange(await upload.mutateAsync({ questionId, file: picked }));
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === "CONTEST_REGISTRATION_FILE_TYPE"
          ? t("fileType")
          : err instanceof ApiError && err.code === "CONTEST_REGISTRATION_FILE_TOO_LARGE"
            ? t("fileTooLarge")
            : t("fileFailed"),
      );
    } finally {
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-1.5">
      <input
        ref={inputRef}
        id={id}
        type="file"
        accept={REGISTRATION_FILE_ACCEPT}
        className="sr-only"
        onChange={(event) => void pick(event.target.files?.[0])}
      />
      <div
        className={cn(
          "flex min-h-16 flex-wrap items-center justify-between gap-3 rounded-xl border-[1.5px] border-dashed bg-white px-4 py-3",
          invalid ? "border-[rgb(var(--destructive))]" : file ? "border-emerald-300" : "border-neutral-300",
        )}
      >
        {file ? (
          <span className="flex min-w-0 items-center gap-2 text-sm text-neutral-800">
            <Paperclip className="h-4 w-4 flex-none text-emerald-600" aria-hidden />
            <span className="truncate font-medium">{file.filename}</span>
            <span className="flex-none text-xs text-neutral-500">{fileSizeLabel(file.sizeBytes)}</span>
          </span>
        ) : (
          <span className="text-xs text-neutral-500">{t("fileHint")}</span>
        )}
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={upload.isPending}
          onClick={() => inputRef.current?.click()}
        >
          {upload.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
          ) : (
            <FileUp className="h-3.5 w-3.5" aria-hidden />
          )}
          {upload.isPending ? t("fileUploading") : file ? t("fileReplace") : t("fileChoose")}
        </Button>
      </div>
      {error ? (
        <p className="text-xs text-[rgb(var(--destructive))]" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
